// Copyright (c) 2026 CTO's Club LLC. All rights reserved.
// Author: Alex Baretta (alex@baretta.com).

/**
 * Authentication API schemas
 */

import { z } from 'zod';
import {
    buildStoredJsonParser,
    defineStoredJsonContract,
    type StoredJsonRowBoundary,
} from '../stored-json-contract.types';
import { OpaqueCursorPaginationSchema } from '../authorization.types';
import { GatewayId, Role, Status } from '@pillar-portal/database/dist/enums';
import { CredentialType } from '../secret.types';
import { IpgSystemId } from '../types/third-party-system.enums';
import { PaymentRequestStatus } from '../payment.types';
import { SingleUseAuthTokenPayloadStorageVersion } from '../auth.types';
import { SystemDisplayTimeZoneSchema } from '../timezone.utils';
import {
    CardDetailsModel,
    CardDetailsModelSchema,
} from '../types/ipg/ipg-card-details-model.types';
import type {
    ActiveRoleAssignmentResponse,
    ActiveRoleAssignmentOperationalResponse,
    AnchorbaseLoginDeepLinkToken,
    CheckProposedPasswordRejectedReason,
    CheckProposedPasswordRequest,
    CheckProposedPasswordResponse,
    CancelNativeAppLoginLinkRequest,
    CompleteCustomerPaymentLinkAttachmentRequest,
    CreateCustomerSelfOnboardingRequest,
    EmailMfaActivationTokenPayload,
    EmailVerificationTokenPayload,
    FirstLoginRequest,
    FirstLoginResponse,
    InvitationTokenPayload,
    ListSingleUseTokensRequest,
    ListSingleUseTokensResponse,
    LoginRequest,
    LoginResponse,
    NativeAppLoginLinkResponse,
    PasswordlessLoginChallengeResponse,
    PasswordlessLoginCodeVerificationRequest,
    PasswordlessLoginLinkExchangeRequest,
    PasswordlessLoginRequest,
    RequestPasswordResetRequest,
    MfaActivationRequestResponse,
    MfaLoginChallengeResponse,
    MfaMethod,
    MfaSetupStatusResponse,
    MfaPolicyStatusResponse,
    PaymentRequestAccessTokenExecutionScope,
    PaymentRequestAccessTokenPaymentConfigurationSnapshot,
    PaymentRequestAccessTokenPayload,
    PillarLoginLinkTokenPayload,
    SingleUseTokenListItem,
    SingleUseTokenOrganizationSummary,
    SingleUseAuthTokenPayload,
    SingleUseAuthTokenPayloadByType,
    SingleUseAuthTokenPayloadStorageV1,
    SingleUseAuthTokenPayloadStorageV2,
    SingleUseTokenPaymentRequestSummary,
    SingleUseTokensSearchRequest,
    SingleUseAuthTokenType,
    SetDefaultRoleAssignmentRequest,
    SetDefaultRoleAssignmentUuidRequest,
    SetActiveRoleAssignmentUuidRequest,
    SetActiveRoleAssignmentResponse,
    SelectableRoleAssignment,
    SelectableRoleAssignmentsResponse,
    SessionUserProfileDetailedResponse,
    SetupTotpResponse,
    SwapCookieForTokenResponse,
    SwapTokenForCookieRequest,
    SwapTokenForCookieResponse,
    TotpSetupActivationTarget,
    SmsMfaActivationTokenPayload,
    SmsVerificationTokenPayload,
    EnableTotpRequest,
    VerifyMfaActivationRequest,
    VerifyEmailResponse,
    VerifyMfaLoginRequest,
    VerifyMfaLoginResponse,
    VerifyTotpRequest,
    VerifyTotpResponse,
} from '../auth.types';
import { PermissionGrantArraySchema, RoleAssignmentDetailedSchema } from '../permission.types';
import { CombinedConfigResponseSchema } from './samba-config.schemas';
import { SearchTermsSchema } from './omnisearch.schemas';
import {
    JsonNonnegativeLosslessNumberInputSchema,
    JsonPercentageLosslessNumberInputSchema,
    PhoneNumberSchema,
} from './common.schemas';
import { UserEmailIdentitySchema } from '../email-identity';
import {
    type SafeIntegerDomain,
    PostgresqlGeneratedBigintIdentifierSafeIntegerDomain,
    StoredJsonNumberRecordSchema,
    convertStoredJsonNumberToSafeInteger,
    defineSafeIntegerDomain,
    normalizeStoredJsonLosslessNumberObjectFields,
    normalizeStoredJsonSafeIntegerObjectFields,
} from '../stored-json-number';

/**
 * Login request
 */
export const LoginRequestSchema: z.ZodType<LoginRequest> = z.object({
    email: UserEmailIdentitySchema,
    password: z.string().min(1),
    payment_request_uuid: z.string().uuid().optional(),
});

export const PasswordlessLoginRequestSchema: z.ZodType<PasswordlessLoginRequest> = z
    .object({
        email: UserEmailIdentitySchema,
        payment_request_uuid: z.string().uuid().optional(),
    })
    .strict();

export const PasswordlessLoginChallengeResponseSchema: z.ZodType<PasswordlessLoginChallengeResponse> =
    z
        .object({
            message: z.string().min(1),
            expires_at: z.string().datetime(),
        })
        .strict();

export const PasswordlessLoginCodeVerificationRequestSchema: z.ZodType<PasswordlessLoginCodeVerificationRequest> =
    z
        .object({
            code: z.string().regex(/^\d{6}$/),
        })
        .strict();

export const PasswordlessLoginLinkTokenSchema: z.ZodType<string> = z
    .string()
    .regex(/^A[A-Za-z0-9_-]{26}$/);

export const PillarLoginLinkLegacyTokenSchema: z.ZodType<string> = z
    .string()
    .regex(/^[a-f0-9]{64}$/);

export const PillarLoginLinkTokenSchema: z.ZodType<string> = z
    .string()
    .regex(/^H[A-Za-z0-9_-]{25}[AQgw]$/);

const AnchorbaseLoginDeepLinkTokenValueSchema: z.ZodType<string> = z
    .string()
    .regex(/^[AH][A-Za-z0-9_-]{26}$/);

const PasswordlessLoginDeepLinkTokenSchema = z
    .object({
        token_kind: z.literal('passwordless_login'),
        token: PasswordlessLoginLinkTokenSchema,
    })
    .strict();

const PillarLoginLinkDeepLinkTokenSchema = z
    .object({
        token_kind: z.literal('pillar_login_link'),
        token: PillarLoginLinkTokenSchema,
    })
    .strict();

export const AnchorbaseLoginDeepLinkTokenSchema: z.ZodType<AnchorbaseLoginDeepLinkToken> =
    z.preprocess(
        (value: unknown): unknown => {
            const parsed = AnchorbaseLoginDeepLinkTokenValueSchema.safeParse(value);
            if (!parsed.success) {
                return value;
            }
            const token: string = parsed.data;
            switch (token[0]) {
                case 'A':
                    return { token_kind: 'passwordless_login', token };
                case 'H':
                    return { token_kind: 'pillar_login_link', token };
            }
            return value;
        },
        z.discriminatedUnion('token_kind', [
            PasswordlessLoginDeepLinkTokenSchema,
            PillarLoginLinkDeepLinkTokenSchema,
        ])
    );

export const PasswordlessLoginLinkTokenContract = {
    variants: {
        v1: {
            normalize: (token: string): string => PasswordlessLoginLinkTokenSchema.parse(token),
        },
    },
    writerVariant: 'v1',
} as const;

export const PillarLoginLinkBearerTokenContract = {
    variants: {
        v1: {
            normalize: (token: string): string => PillarLoginLinkLegacyTokenSchema.parse(token),
        },
        v2: {
            normalize: (token: string): string => PillarLoginLinkTokenSchema.parse(token),
        },
    },
    writerVariant: 'v2',
} as const;

export const PasswordlessLoginLinkExchangeRequestSchema: z.ZodType<PasswordlessLoginLinkExchangeRequest> =
    z
        .object({
            token: PasswordlessLoginLinkTokenSchema,
        })
        .strict();

/**
 * Login response
 * If requires_totp is true, user must verify TOTP before being fully authenticated.
 * If requires_totp_setup is true, the authenticated user must enable TOTP
 * before continuing under the already-activated role assignment.
 */
export const TotpSetupActivationTargetSchema: z.ZodType<TotpSetupActivationTarget> = z
    .object({
        role_assignment_uuid: z.string().uuid(),
        organization_uuid: z.string().uuid().nullable(),
        organization_name: z.string().nullable(),
    })
    .strict();

export const MfaMethodSchema: z.ZodType<MfaMethod> = z.enum(['TOTP', 'EMAIL', 'SMS']);

export const MfaSetupStatusResponseSchema: z.ZodType<MfaSetupStatusResponse> = z
    .object({
        activation_target: TotpSetupActivationTargetSchema.nullable(),
        available_mfa_setup_methods: z.array(MfaMethodSchema),
    })
    .strict();

const LoginResponseShape = {
    message: z.string(),
    requires_totp: z.boolean(),
    requires_mfa: z.boolean().default(false),
    available_mfa_methods: z.array(MfaMethodSchema).default([]),
    requires_mfa_setup: z.boolean().default(false),
    available_mfa_setup_methods: z.array(MfaMethodSchema).default([]),
    requires_totp_setup: z.boolean().default(false),
    requires_password_change: z.boolean().default(false),
    mfa_setup_activation_target: TotpSetupActivationTargetSchema.optional(),
    totp_setup_activation_target: TotpSetupActivationTargetSchema.optional(),
    user: z
        .object({
            user_uuid: z.string().uuid(),
            primary_email: z.string(),
            emails: z.array(z.string()),
            status: z.enum(Status),
            totp_enabled: z.boolean(),
        })
        .optional(),
};

export const LoginResponseSchema: z.ZodType<LoginResponse> = z.object(LoginResponseShape);

export const FirstLoginRequestSchema: z.ZodType<FirstLoginRequest> = z
    .object({
        token: z.string().min(1),
    })
    .strict();

export const FirstLoginResponseSchema: z.ZodType<FirstLoginResponse> = z
    .object({
        ...LoginResponseShape,
        message: z.literal('First login successful'),
    })
    .strict();

export const MfaPolicyStatusResponseSchema: z.ZodType<MfaPolicyStatusResponse> = z
    .object({
        required: z.boolean(),
        totp_enabled: z.boolean(),
        email_allowed: z.boolean(),
        sms_allowed: z.boolean(),
        available_login_methods: z.array(MfaMethodSchema),
        primary_email_available: z.boolean(),
        primary_phone_available: z.boolean(),
        can_disable: z.boolean(),
    })
    .strict();

export const MfaActivationRequestResponseSchema: z.ZodType<MfaActivationRequestResponse> = z
    .object({
        message: z.string().min(1),
        delivery_destination: z.string().min(1),
        expires_at: z.string().datetime(),
    })
    .strict();

export const MfaLoginChallengeResponseSchema: z.ZodType<MfaLoginChallengeResponse> =
    MfaActivationRequestResponseSchema;

export const VerifyMfaActivationRequestSchema: z.ZodType<VerifyMfaActivationRequest> = z
    .object({
        code: z.string().min(1),
    })
    .strict();

export const VerifyMfaLoginRequestSchema: z.ZodType<VerifyMfaLoginRequest> =
    VerifyMfaActivationRequestSchema;

export const EmailMfaActivationTokenPayloadSchema: z.ZodType<EmailMfaActivationTokenPayload> = z
    .object({
        user_id: z.number().int().positive(),
        email: z.string().email(),
    })
    .strict();

export const SmsMfaActivationTokenPayloadSchema: z.ZodType<SmsMfaActivationTokenPayload> = z
    .object({
        user_id: z.number().int().positive(),
        phone: z.string().min(1),
    })
    .strict();

export const SmsVerificationTokenPayloadSchema: z.ZodType<SmsVerificationTokenPayload> = z
    .object({
        user_id: z.number().int().positive(),
        phone: z.string().min(1),
    })
    .strict();

export const SessionUserProfileDetailedResponseSchema: z.ZodType<SessionUserProfileDetailedResponse> =
    z.object({
        user_uuid: z.string().uuid(),
        first_name: z.string().nullable(),
        last_name: z.string().nullable(),
        primary_email: z.string(),
        emails: z.array(z.string()),
        phones: z.array(z.string()).default([]),
        primary_phone: z.string().nullable().default(null),
        totp_enabled: z.boolean(),
        mfa_email_allowed: z.boolean().default(false),
        mfa_sms_allowed: z.boolean().default(false),
        status: z.enum(Status),
        requires_password_change: z.boolean().default(false),
        invitation_token: z.string().optional(),
        default_role_assignment: RoleAssignmentDetailedSchema.nullable(),
        other_role_assignments: z.array(RoleAssignmentDetailedSchema),
    });

export const ActivePermissionsBundleResponseSchema: z.ZodType<
    ActiveRoleAssignmentOperationalResponse['permissions_bundle']
> = z.object({
    permissions_bundle_uuid: z.string().uuid(),
    name: z.string(),
    description: z.string().nullable(),
    permissions: PermissionGrantArraySchema,
    is_personal: z.boolean(),
    status: z.enum(Status),
});

export const ActiveRoleAssignmentResponseSchema: z.ZodType<ActiveRoleAssignmentResponse> =
    z.discriminatedUnion('activation_state', [
        z
            .object({
                activation_state: z.literal('operational'),
                role_assignment_uuid: z.string().uuid(),
                organization_uuid: z.string().uuid().nullable(),
                status: z.enum(Status),
                role: z.enum(Role),
                organization_name: z.string().nullable(),
                organization_is_iso: z.boolean(),
                organization_is_merchant: z.boolean(),
                organization_is_customer: z.boolean(),
                permissions_bundle: ActivePermissionsBundleResponseSchema,
                combined_config: CombinedConfigResponseSchema,
            })
            .strict(),
        z
            .object({
                activation_state: z.literal('legal_acceptance_required'),
                role_assignment_uuid: z.string().uuid(),
                role: z.enum(Role),
                organization_uuid: z.string().uuid(),
                organization_name: z.string(),
                legal_document_assignment_uuid: z.string().uuid(),
                legal_document_uuid: z.string().uuid(),
            })
            .strict(),
    ]);

export const CompleteCustomerPaymentLinkAttachmentRequestSchema: z.ZodType<CompleteCustomerPaymentLinkAttachmentRequest> =
    z.object({
        payment_request_uuid: z.string().uuid(),
    });

export const CheckProposedPasswordRejectedReasonSchema: z.ZodType<CheckProposedPasswordRejectedReason> =
    z.union([
        z.literal('PASSWORD_TOO_LONG'),
        z.literal('DISALLOWED_DEFAULT_PASSWORD'),
        z.literal('ZXCVBN_TOO_WEAK'),
        z.literal('COMPROMISED_PASSWORD_CORPUS_MATCH'),
        z.literal('COMPROMISED_PASSWORD_CORPUS_UNAVAILABLE'),
    ]);

export const CheckProposedPasswordRequestSchema: z.ZodType<CheckProposedPasswordRequest> = z
    .object({
        password: z.string().min(1),
    })
    .strict();

export const CheckProposedPasswordResponseSchema: z.ZodType<CheckProposedPasswordResponse> = z
    .object({
        accepted: z.boolean(),
        rejected_reasons: z.array(CheckProposedPasswordRejectedReasonSchema),
    })
    .strict();

/**
 * Verify TOTP request
 */
export const VerifyTotpRequestSchema: z.ZodType<VerifyTotpRequest> = z.object({
    totp_code: z.string().min(1),
});

/**
 * Verify TOTP response
 */
export const VerifyTotpResponseSchema: z.ZodType<VerifyTotpResponse> = z.object({
    user_uuid: z.string().uuid(),
    primary_email: z.string(),
    roles: z.array(z.string()),
});

export const VerifyMfaLoginResponseSchema: z.ZodType<VerifyMfaLoginResponse> =
    VerifyTotpResponseSchema;

/**
 * Verify email request
 */
export const VerifyEmailRequestSchema = z.object({
    token: z.string().min(1),
    password: z.string().optional(), // Required for PENDING users
});

/**
 * Verify email response
 */
export const VerifyEmailResponseSchema: z.ZodType<VerifyEmailResponse> = z.object({
    message: z.string(),
    user: z.object({
        user_uuid: z.string().uuid(),
        primary_email: z.string().nullable(),
        status: z.enum(Status),
    }),
});

export const CreateCustomerSelfOnboardingRequestSchema: z.ZodType<CreateCustomerSelfOnboardingRequest> =
    z.object({
        email: UserEmailIdentitySchema,
        first_name: z.string().trim().min(1),
        last_name: z.string().trim().min(1),
        password: z.string().min(8),
        payment_request_uuid: z.string().uuid().optional(),
        store_payment_method_for_future_use: z.boolean().optional(),
    });

export const CreateCustomerSelfOnboardingResponseSchema = z.object({
    message: z.string(),
    primary_email: z.string().email(),
    customer_organization_uuid: z.string().uuid(),
});

function normalizeStoredSafeIntegerArray(
    value: unknown,
    safeIntegerDomain: SafeIntegerDomain
): unknown {
    if (!Array.isArray(value)) {
        return value;
    }
    return value.map((element: unknown): unknown => {
        const conversionResult = convertStoredJsonNumberToSafeInteger(element, safeIntegerDomain);
        return conversionResult.success ? conversionResult.value : element;
    });
}

const GatewayIdentifierSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'single-use-auth-token.gateway_id',
    provenance: {
        kind: 'finite-integer-domain',
        values: Object.values(GatewayId),
    },
});

const IpgSystemIdentifierSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'single-use-auth-token.ipg_id',
    provenance: {
        kind: 'finite-integer-domain',
        values: Object.values(IpgSystemId),
    },
});

const SingleUseAuthTokenPayloadStorageVersionSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'single-use-auth-token.single_use_auth_token_payload_version',
    provenance: {
        kind: 'finite-integer-domain',
        values: Object.values(SingleUseAuthTokenPayloadStorageVersion),
    },
});

function normalizeStoredInvitationTokenPayload(value: unknown): unknown {
    const normalizedRoot = normalizeStoredJsonSafeIntegerObjectFields(value, {
        safeIntegerDomain: PostgresqlGeneratedBigintIdentifierSafeIntegerDomain,
        fields: ['organization_id'],
    });
    const rootResult = StoredJsonNumberRecordSchema.safeParse(normalizedRoot);
    if (!rootResult.success || !Object.hasOwn(rootResult.data, 'policy_bundle_ids')) {
        return normalizedRoot;
    }
    return {
        ...rootResult.data,
        policy_bundle_ids: normalizeStoredSafeIntegerArray(
            rootResult.data.policy_bundle_ids,
            PostgresqlGeneratedBigintIdentifierSafeIntegerDomain
        ),
    };
}

export const InvitationTokenPayloadSchema: z.ZodType<InvitationTokenPayload> = z.object({
    primary_email: z.string().email(),
    role: z.enum(Role),
    organization_id: z.number().int().positive().nullable(),
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    emails: z.array(z.string().email()).optional(),
    phones: z.array(z.string().min(1)).optional(),
    primary_phone: z.string().min(1).nullable().optional(),
    policies: z.array(z.string().uuid()).optional(),
    policy_bundle_ids: z.array(z.number().int().safe().positive()).optional(),
});

export const EmailVerificationTokenPayloadSchema: z.ZodType<EmailVerificationTokenPayload> =
    z.object({
        user_id: z.number().int().positive(),
        email: z.string().email(),
    });

export const StoredInvitationTokenPayloadInputSchema: z.ZodType<InvitationTokenPayload | null> =
    InvitationTokenPayloadSchema.nullable();

export const StoredInvitationTokenPayloadSchema: z.ZodType<InvitationTokenPayload | null> =
    z.preprocess(normalizeStoredInvitationTokenPayload, StoredInvitationTokenPayloadInputSchema);

export const StoredEmailVerificationTokenPayloadSchema: z.ZodType<EmailVerificationTokenPayload> =
    z.preprocess(
        (value: unknown): unknown =>
            normalizeStoredJsonSafeIntegerObjectFields(value, {
                safeIntegerDomain: PostgresqlGeneratedBigintIdentifierSafeIntegerDomain,
                fields: ['user_id'],
            }),
        EmailVerificationTokenPayloadSchema
    );

export const StoredSmsVerificationTokenPayloadSchema: z.ZodType<SmsVerificationTokenPayload> =
    z.preprocess(
        (value: unknown): unknown =>
            normalizeStoredJsonSafeIntegerObjectFields(value, {
                safeIntegerDomain: PostgresqlGeneratedBigintIdentifierSafeIntegerDomain,
                fields: ['user_id'],
            }),
        SmsVerificationTokenPayloadSchema
    );

export const StoredEmailMfaActivationTokenPayloadSchema: z.ZodType<EmailMfaActivationTokenPayload> =
    z.preprocess(
        (value: unknown): unknown =>
            normalizeStoredJsonSafeIntegerObjectFields(value, {
                safeIntegerDomain: PostgresqlGeneratedBigintIdentifierSafeIntegerDomain,
                fields: ['user_id'],
            }),
        EmailMfaActivationTokenPayloadSchema
    );

export const StoredSmsMfaActivationTokenPayloadSchema: z.ZodType<SmsMfaActivationTokenPayload> =
    z.preprocess(
        (value: unknown): unknown =>
            normalizeStoredJsonSafeIntegerObjectFields(value, {
                safeIntegerDomain: PostgresqlGeneratedBigintIdentifierSafeIntegerDomain,
                fields: ['user_id'],
            }),
        SmsMfaActivationTokenPayloadSchema
    );

export const PaymentRequestAccessTokenPaymentConfigurationSnapshotSchema = z.object({
    account_id: z.string().min(1),
    credential_uuid: z.string().uuid(),
    gateway_id: z.union([
        z.literal(GatewayId.VALOR_US),
        z.literal(GatewayId.CLOVER),
        z.literal(GatewayId.CARDPOINTE),
    ]),
    credential_type: z.enum([CredentialType.VIRTUAL_TERMINAL, CredentialType.PHYSICAL_TERMINAL]),
    is_virtual: z.boolean().nullable(),
    card_details_model: CardDetailsModelSchema,
    allow_surcharge: z.boolean(),
    allow_convenience_fee: z.boolean(),
    allow_override_fee: z.boolean(),
    addressRequiredForCardPayment: z.boolean().default(true),
    surcharge_pct: JsonPercentageLosslessNumberInputSchema.nullable(),
    convenience_fee: JsonNonnegativeLosslessNumberInputSchema.nullable(),
}) satisfies z.ZodType<PaymentRequestAccessTokenPaymentConfigurationSnapshot>;

const UnknownRecordSchema = z.record(z.string(), z.unknown());

function normalizeStoredPaymentRequestAccessTokenPaymentConfiguration(value: unknown): unknown {
    const normalizedIdentifiers = normalizeStoredJsonSafeIntegerObjectFields(value, {
        safeIntegerDomain: GatewayIdentifierSafeIntegerDomain,
        fields: ['gateway_id'],
    });
    const normalizedConfiguration = normalizeStoredJsonLosslessNumberObjectFields(
        normalizedIdentifiers,
        { fields: ['surcharge_pct', 'convenience_fee'] }
    );
    const configurationResult = UnknownRecordSchema.safeParse(normalizedConfiguration);
    if (!configurationResult.success) {
        return normalizedConfiguration;
    }
    const configuration = configurationResult.data;
    if (configuration.card_details_model !== undefined) {
        return configuration;
    }
    switch (configuration.gateway_id ?? GatewayId.VALOR_US) {
        case GatewayId.VALOR_US:
            return {
                ...configuration,
                card_details_model: CardDetailsModel.FULLY_OWNED,
            };
        case GatewayId.CARDPOINTE:
            return {
                ...configuration,
                card_details_model: CardDetailsModel.HOSTED_IFRAME,
            };
        case GatewayId.CLOVER:
            return {
                ...configuration,
                card_details_model: CardDetailsModel.HOSTED_FIELDS,
            };
        default:
            return configuration;
    }
}

export const StoredPaymentRequestAccessTokenPaymentConfigurationSnapshotInputSchema: z.ZodType<PaymentRequestAccessTokenPaymentConfigurationSnapshot> =
    z.object({
        account_id: z.string().min(1),
        credential_uuid: z.string().uuid(),
        gateway_id: z
            .union([
                z.literal(GatewayId.VALOR_US),
                z.literal(GatewayId.CLOVER),
                z.literal(GatewayId.CARDPOINTE),
            ])
            .default(GatewayId.VALOR_US),
        credential_type: z.enum([
            CredentialType.VIRTUAL_TERMINAL,
            CredentialType.PHYSICAL_TERMINAL,
        ]),
        is_virtual: z.boolean().nullable(),
        card_details_model: CardDetailsModelSchema,
        allow_surcharge: z.boolean(),
        allow_convenience_fee: z.boolean(),
        allow_override_fee: z.boolean(),
        addressRequiredForCardPayment: z.boolean().default(true),
        surcharge_pct: JsonPercentageLosslessNumberInputSchema.nullable(),
        convenience_fee: JsonNonnegativeLosslessNumberInputSchema.nullable(),
    });

export const StoredPaymentRequestAccessTokenPaymentConfigurationSnapshotParser: z.ZodType<PaymentRequestAccessTokenPaymentConfigurationSnapshot> =
    z.preprocess(
        normalizeStoredPaymentRequestAccessTokenPaymentConfiguration,
        StoredPaymentRequestAccessTokenPaymentConfigurationSnapshotInputSchema
    );

export const PaymentRequestAccessTokenExecutionScopeSchema: z.ZodType<PaymentRequestAccessTokenExecutionScope> =
    z.discriminatedUnion('ipg_id', [
        z.object({
            ipg_id: z.literal(IpgSystemId.VALOR_US),
            terminal_device_id: z.number().int().positive(),
            payment_configuration:
                PaymentRequestAccessTokenPaymentConfigurationSnapshotSchema.extend({
                    gateway_id: z.literal(GatewayId.VALOR_US),
                }),
        }),
        z.object({
            ipg_id: z.literal(IpgSystemId.CLOVER),
            terminal_device_id: z.number().int().positive(),
            payment_configuration:
                PaymentRequestAccessTokenPaymentConfigurationSnapshotSchema.extend({
                    gateway_id: z.literal(GatewayId.CLOVER),
                }),
        }),
        z.object({
            ipg_id: z.literal(IpgSystemId.CARDPOINTE),
            terminal_device_id: z.number().int().positive(),
            payment_configuration:
                PaymentRequestAccessTokenPaymentConfigurationSnapshotSchema.extend({
                    gateway_id: z.literal(GatewayId.CARDPOINTE),
                }),
        }),
    ]);

export const StoredPaymentRequestAccessTokenExecutionScopeInputSchema: z.ZodType<PaymentRequestAccessTokenExecutionScope> =
    z.discriminatedUnion('ipg_id', [
        z.object({
            ipg_id: z.literal(IpgSystemId.VALOR_US),
            terminal_device_id: z.number().int().positive(),
            payment_configuration: z.preprocess(
                normalizeStoredPaymentRequestAccessTokenPaymentConfiguration,
                PaymentRequestAccessTokenPaymentConfigurationSnapshotSchema.extend({
                    gateway_id: z.literal(GatewayId.VALOR_US).default(GatewayId.VALOR_US),
                })
            ),
        }),
        z.object({
            ipg_id: z.literal(IpgSystemId.CLOVER),
            terminal_device_id: z.number().int().positive(),
            payment_configuration: z.preprocess(
                normalizeStoredPaymentRequestAccessTokenPaymentConfiguration,
                PaymentRequestAccessTokenPaymentConfigurationSnapshotSchema.extend({
                    gateway_id: z.literal(GatewayId.CLOVER),
                })
            ),
        }),
        z.object({
            ipg_id: z.literal(IpgSystemId.CARDPOINTE),
            terminal_device_id: z.number().int().positive(),
            payment_configuration: z.preprocess(
                normalizeStoredPaymentRequestAccessTokenPaymentConfiguration,
                PaymentRequestAccessTokenPaymentConfigurationSnapshotSchema.extend({
                    gateway_id: z.literal(GatewayId.CARDPOINTE),
                })
            ),
        }),
    ]);

export const StoredPaymentRequestAccessTokenExecutionScopeParser: z.ZodType<PaymentRequestAccessTokenExecutionScope> =
    z.preprocess((value: unknown): unknown => {
        const normalizedIpgIdentifier = normalizeStoredJsonSafeIntegerObjectFields(value, {
            safeIntegerDomain: IpgSystemIdentifierSafeIntegerDomain,
            fields: ['ipg_id'],
        });
        return normalizeStoredJsonSafeIntegerObjectFields(normalizedIpgIdentifier, {
            safeIntegerDomain: PostgresqlGeneratedBigintIdentifierSafeIntegerDomain,
            fields: ['terminal_device_id'],
        });
    }, StoredPaymentRequestAccessTokenExecutionScopeInputSchema);

export const PaymentRequestAccessTokenPayloadSchema: z.ZodType<PaymentRequestAccessTokenPayload> =
    z.object({
        payment_request_uuid: z.string().uuid(),
        payment_execution_scope: PaymentRequestAccessTokenExecutionScopeSchema,
    });

export const StoredPaymentRequestAccessTokenPayloadParser: z.ZodType<PaymentRequestAccessTokenPayload> =
    z.object({
        payment_request_uuid: z.string().uuid(),
        payment_execution_scope: StoredPaymentRequestAccessTokenExecutionScopeParser,
    });

export const PillarLoginLinkTokenPayloadSchema: z.ZodType<PillarLoginLinkTokenPayload> = z
    .object({
        active_role_assignment_id: z.number().int().positive(),
        impersonated_role_assignment_id: z.number().int().positive().optional(),
    })
    .strict();

export const StoredPillarLoginLinkTokenPayloadSchema: z.ZodType<PillarLoginLinkTokenPayload> =
    z.preprocess(
        (value: unknown): unknown =>
            normalizeStoredJsonSafeIntegerObjectFields(value, {
                safeIntegerDomain: PostgresqlGeneratedBigintIdentifierSafeIntegerDomain,
                fields: ['active_role_assignment_id', 'impersonated_role_assignment_id'],
            }),
        PillarLoginLinkTokenPayloadSchema
    );

export const SingleUseAuthTokenTypeSchema: z.ZodType<SingleUseAuthTokenType> = z.enum([
    'INVITATION',
    'PASSWORD_RESET',
    'EMAIL_VERIFICATION',
    'SMS_VERIFICATION',
    'PAYMENT_REQUEST_ACCESS',
    'PILLAR_LOGIN_LINK',
    'MFA_ENABLE_EMAIL',
    'MFA_ENABLE_SMS',
    'MFA_LOGIN_EMAIL',
    'MFA_LOGIN_SMS',
]);

export const SingleUseAuthTokenPayloadVersionedObjectMarkerSchema: z.ZodType<{
    single_use_auth_token_payload_version: unknown;
}> = z
    .object({
        single_use_auth_token_payload_version: z.unknown(),
    })
    .passthrough();

export const SingleUseAuthTokenPayloadStorageEnvelopeInputSchema: z.ZodType<{
    type: SingleUseAuthTokenType;
    payload: unknown;
}> = z
    .object({
        type: SingleUseAuthTokenTypeSchema,
        payload: z.unknown(),
    })
    .strict();

export const SingleUseAuthTokenPayloadSchemaByType: {
    readonly [Type in SingleUseAuthTokenType]: z.ZodType<SingleUseAuthTokenPayloadByType[Type]>;
} = {
    INVITATION: InvitationTokenPayloadSchema.nullable(),
    PASSWORD_RESET: z.null(),
    EMAIL_VERIFICATION: EmailVerificationTokenPayloadSchema,
    SMS_VERIFICATION: SmsVerificationTokenPayloadSchema,
    PAYMENT_REQUEST_ACCESS: PaymentRequestAccessTokenPayloadSchema,
    PILLAR_LOGIN_LINK: PillarLoginLinkTokenPayloadSchema,
    MFA_ENABLE_EMAIL: EmailMfaActivationTokenPayloadSchema,
    MFA_ENABLE_SMS: SmsMfaActivationTokenPayloadSchema,
    MFA_LOGIN_EMAIL: EmailMfaActivationTokenPayloadSchema,
    MFA_LOGIN_SMS: SmsMfaActivationTokenPayloadSchema,
};

export const StoredSingleUseAuthTokenPayloadSchemaByType: {
    readonly [Type in SingleUseAuthTokenType]: z.ZodType<SingleUseAuthTokenPayloadByType[Type]>;
} = {
    ...SingleUseAuthTokenPayloadSchemaByType,
    INVITATION: StoredInvitationTokenPayloadSchema,
    EMAIL_VERIFICATION: StoredEmailVerificationTokenPayloadSchema,
    SMS_VERIFICATION: StoredSmsVerificationTokenPayloadSchema,
    PAYMENT_REQUEST_ACCESS: StoredPaymentRequestAccessTokenPayloadParser,
    PILLAR_LOGIN_LINK: StoredPillarLoginLinkTokenPayloadSchema,
    MFA_ENABLE_EMAIL: StoredEmailMfaActivationTokenPayloadSchema,
    MFA_ENABLE_SMS: StoredSmsMfaActivationTokenPayloadSchema,
    MFA_LOGIN_EMAIL: StoredEmailMfaActivationTokenPayloadSchema,
    MFA_LOGIN_SMS: StoredSmsMfaActivationTokenPayloadSchema,
};

export interface StoredSingleUseAuthTokenPayloadV2Envelope {
    readonly single_use_auth_token_payload_version: typeof SingleUseAuthTokenPayloadStorageVersion.V2;
    readonly payload: unknown;
}

export const StoredSingleUseAuthTokenPayloadV2EnvelopeInputSchema: z.ZodType<StoredSingleUseAuthTokenPayloadV2Envelope> =
    z
        .object({
            single_use_auth_token_payload_version: z.literal(
                SingleUseAuthTokenPayloadStorageVersion.V2
            ),
            payload: z.unknown(),
        })
        .strict();

export const StoredSingleUseAuthTokenPayloadV2EnvelopeSchema: z.ZodType<StoredSingleUseAuthTokenPayloadV2Envelope> =
    z.preprocess(
        (value: unknown): unknown =>
            normalizeStoredJsonSafeIntegerObjectFields(value, {
                safeIntegerDomain: SingleUseAuthTokenPayloadStorageVersionSafeIntegerDomain,
                fields: ['single_use_auth_token_payload_version'],
            }),
        StoredSingleUseAuthTokenPayloadV2EnvelopeInputSchema
    );

function getSingleUseAuthTokenPayloadSchema<Type extends SingleUseAuthTokenType>(
    type: Type
): z.ZodType<SingleUseAuthTokenPayloadByType[Type]> {
    return SingleUseAuthTokenPayloadSchemaByType[type];
}

function getStoredSingleUseAuthTokenPayloadSchema<Type extends SingleUseAuthTokenType>(
    type: Type
): z.ZodType<SingleUseAuthTokenPayloadByType[Type]> {
    return StoredSingleUseAuthTokenPayloadSchemaByType[type];
}

export function parseStoredSingleUseAuthTokenPayload<Type extends SingleUseAuthTokenType>(
    type: Type,
    payload: unknown
): SingleUseAuthTokenPayloadByType[Type] {
    const payloadSchema = getStoredSingleUseAuthTokenPayloadSchema(type);
    const v2ParseResult = StoredSingleUseAuthTokenPayloadV2EnvelopeSchema.safeParse(payload);
    if (v2ParseResult.success) {
        return payloadSchema.parse(v2ParseResult.data.payload);
    }

    const versionedObjectParseResult =
        SingleUseAuthTokenPayloadVersionedObjectMarkerSchema.safeParse(payload);
    if (versionedObjectParseResult.success) {
        throw v2ParseResult.error;
    }

    return payloadSchema.parse(payload);
}

export const StoredSingleUseAuthTokenPayloadParser: z.ZodType<SingleUseAuthTokenPayload> =
    SingleUseAuthTokenPayloadStorageEnvelopeInputSchema.transform(
        (value, ctx): SingleUseAuthTokenPayload => {
            try {
                return parseStoredSingleUseAuthTokenPayload(value.type, value.payload);
            } catch (error: unknown) {
                ctx.addIssue({
                    code: 'custom',
                    message: error instanceof Error ? error.message : 'Invalid token payload',
                });
                return z.NEVER;
            }
        }
    );

export const StoredSingleUseAuthTokenPayloadV1RowSchema: z.ZodType<SingleUseAuthTokenPayload> =
    SingleUseAuthTokenPayloadStorageEnvelopeInputSchema.transform(
        (value): SingleUseAuthTokenPayload =>
            getStoredSingleUseAuthTokenPayloadSchema(value.type).parse(value.payload)
    );

export const StoredSingleUseAuthTokenPayloadV2RowSchema: z.ZodType<SingleUseAuthTokenPayload> =
    SingleUseAuthTokenPayloadStorageEnvelopeInputSchema.transform(
        (value): SingleUseAuthTokenPayload => {
            const envelope = StoredSingleUseAuthTokenPayloadV2EnvelopeSchema.parse(value.payload);
            return getStoredSingleUseAuthTokenPayloadSchema(value.type).parse(envelope.payload);
        }
    );

export function normalizeSingleUseAuthTokenPayloadStorageV1(
    payload: SingleUseAuthTokenPayload
): SingleUseAuthTokenPayload {
    return payload;
}

export function normalizeSingleUseAuthTokenPayloadStorageV2(
    payload: SingleUseAuthTokenPayload
): SingleUseAuthTokenPayload {
    return payload;
}

export const SingleUseAuthTokenPayloadStorageContract = defineStoredJsonContract({
    contractName: 'SingleUseAuthTokenPayload',
    semanticFieldValues: {},
    variants: {
        v1: {
            schemaName: 'StoredSingleUseAuthTokenPayloadV1RowSchema',
            schema: StoredSingleUseAuthTokenPayloadV1RowSchema,
            schemaInput: (boundary: StoredJsonRowBoundary) => ({
                type: boundary.siblingValues.type,
                payload: boundary.value,
            }),
            guards: [
                {
                    kind: 'json_marker_absent',
                    markerPath: ['single_use_auth_token_payload_version'],
                },
                {
                    kind: 'sibling_field_present',
                    siblingField: 'type',
                },
            ],
            normalize: normalizeSingleUseAuthTokenPayloadStorageV1,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
        v2: {
            schemaName: 'StoredSingleUseAuthTokenPayloadV2RowSchema',
            schema: StoredSingleUseAuthTokenPayloadV2RowSchema,
            schemaInput: (boundary: StoredJsonRowBoundary) => ({
                type: boundary.siblingValues.type,
                payload: boundary.value,
            }),
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['single_use_auth_token_payload_version'],
                    markerValue: SingleUseAuthTokenPayloadStorageVersion.V2,
                },
                {
                    kind: 'sibling_field_present',
                    siblingField: 'type',
                },
            ],
            normalize: normalizeSingleUseAuthTokenPayloadStorageV2,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v2',
});

export const StoredSingleUseAuthTokenPayloadRowParser: z.ZodType<SingleUseAuthTokenPayload> =
    buildStoredJsonParser(SingleUseAuthTokenPayloadStorageContract);

export function serializeSingleUseAuthTokenPayloadV2<Type extends SingleUseAuthTokenType>(
    type: Type,
    payload: SingleUseAuthTokenPayloadStorageV1<Type>
): SingleUseAuthTokenPayloadStorageV2<Type> {
    return {
        single_use_auth_token_payload_version: SingleUseAuthTokenPayloadStorageVersion.V2,
        payload: getSingleUseAuthTokenPayloadSchema(type).parse(payload),
    };
}

export const SingleUseTokenPaymentRequestSummarySchema: z.ZodType<SingleUseTokenPaymentRequestSummary> =
    z.object({
        payment_request_uuid: z.string().uuid(),
        merchant_organization_uuid: z.string().uuid(),
        status: z.enum(PaymentRequestStatus),
        customer_name: z.string().optional(),
        recipient_email: z.string().email().optional(),
        recipient_phone: PhoneNumberSchema.optional(),
        original_amount: z.number().optional(),
        currency_code: z.string().min(1).optional(),
    });

export const SingleUseTokenOrganizationSummarySchema: z.ZodType<SingleUseTokenOrganizationSummary> =
    z.object({
        organization_uuid: z.string().uuid(),
        name: z.string().min(1),
    });

export const SingleUseTokenListItemSchema: z.ZodType<SingleUseTokenListItem> = z.object({
    single_use_auth_token_uuid: z.string().uuid(),
    type: SingleUseAuthTokenTypeSchema,
    user_uuid: z.string().uuid().nullable(),
    user_primary_email: z.string().nullable(),
    email: z.string().email().nullable(),
    organizations: z.array(SingleUseTokenOrganizationSummarySchema),
    payment_request: SingleUseTokenPaymentRequestSummarySchema.optional(),
    created_ts: z.string().datetime(),
    expire_ts: z.string().datetime(),
    used_ts: z.string().datetime().nullable(),
    is_outstanding: z.boolean(),
    is_expired: z.boolean(),
});

export const ListSingleUseTokensRequestSchema: z.ZodType<ListSingleUseTokensRequest> = z.object({
    outstanding_only: z.boolean(),
    q: z.string().trim().optional(),
    limit: z.number().int().positive().max(500).optional(),
    cursor: z.string().uuid().optional(),
});

export const SingleUseTokensSearchRequestSchema: z.ZodType<SingleUseTokensSearchRequest> = z
    .object({
        outstanding_only: z.boolean(),
        terms: SearchTermsSchema,
        limit: z.number().int().positive().max(500).optional(),
        cursor: z.string().uuid().optional(),
    })
    .strict();

export const ListSingleUseTokensResponseSchema: z.ZodType<ListSingleUseTokensResponse> = z.object({
    tokens: z.array(SingleUseTokenListItemSchema),
    pagination: OpaqueCursorPaginationSchema,
    display_timezone: SystemDisplayTimeZoneSchema,
});

export const SingleUseAuthTokenUuidParamSchema: z.ZodType<string> = z.string().uuid();

export const SwapCookieForTokenResponseSchema: z.ZodType<SwapCookieForTokenResponse> = z
    .object({
        token: z.union([PillarLoginLinkLegacyTokenSchema, PillarLoginLinkTokenSchema]),
        expires_at: z.string().datetime(),
    })
    .strict();

export const AnchorbaseNativeAppLoginUrlSchema = z
    .string()
    .regex(/^anchorbase:\/\/login\/passwordless#H[A-Za-z0-9_-]{25}[AQgw]$/);

export const NativeAppLoginLinkResponseSchema: z.ZodType<NativeAppLoginLinkResponse> = z
    .object({
        application_url: AnchorbaseNativeAppLoginUrlSchema,
        expires_at: z.string().datetime(),
    })
    .strict();

export const CancelNativeAppLoginLinkRequestSchema: z.ZodType<CancelNativeAppLoginLinkRequest> = z
    .object({
        application_url: AnchorbaseNativeAppLoginUrlSchema,
    })
    .strict();

export const SwapTokenForCookieRequestSchema: z.ZodType<SwapTokenForCookieRequest> = z
    .object({
        token: z.union([PillarLoginLinkLegacyTokenSchema, PillarLoginLinkTokenSchema]),
    })
    .strict();

export const SwapTokenForCookieResponseSchema: z.ZodType<SwapTokenForCookieResponse> =
    LoginResponseSchema;

/**
 * Change password request
 */
export const ChangePasswordRequestSchema = z.object({
    old_password: z.string().min(1),
    new_password: z.string().min(1),
});

/**
 * Setup TOTP response
 */
export const SetupTotpResponseSchema: z.ZodType<SetupTotpResponse> = z.object({
    secret: z.string(),
    qr_code: z.string(),
    message: z.string(),
});

/**
 * Enable TOTP request
 */
export const EnableTotpRequestSchema: z.ZodType<EnableTotpRequest> = z.object({
    secret: z.string(),
    code: z.string().min(1),
});

/**
 * Generate backup codes response
 */
export const GenerateBackupCodesResponseSchema = z.object({
    codes: z.array(z.string()),
});

export const SetDefaultRoleAssignmentUuidRequestSchema: z.ZodType<SetDefaultRoleAssignmentUuidRequest> =
    z.object({
        role_assignment_uuid: z.string().uuid(),
    });

/**
 * Set default role assignment request
 */
export const SetDefaultRoleAssignmentRequestSchema: z.ZodType<SetDefaultRoleAssignmentRequest> =
    SetDefaultRoleAssignmentUuidRequestSchema;

/**
 * Set active role assignment request
 */
export const SetActiveRoleAssignmentRequestSchema = z.object({
    role_assignment_id: z.number(),
});

export const SetActiveRoleAssignmentUuidRequestSchema: z.ZodType<SetActiveRoleAssignmentUuidRequest> =
    z.object({
        role_assignment_uuid: z.string().uuid(),
    });

export const SelectableRoleAssignmentSchema: z.ZodType<SelectableRoleAssignment> = z
    .object({
        role_assignment_uuid: z.string().uuid(),
        role: z.enum(Role),
        organization_uuid: z.string().uuid().nullable(),
        organization_name: z.string().nullable(),
    })
    .strict()
    .superRefine((roleAssignment, context) => {
        if (
            (roleAssignment.organization_uuid === null) !==
            (roleAssignment.organization_name === null)
        ) {
            context.addIssue({
                code: 'custom',
                message: 'Organization UUID and name must either both be null or both be present',
            });
        }
    });

export const SelectableRoleAssignmentsResponseSchema: z.ZodType<SelectableRoleAssignmentsResponse> =
    z
        .object({
            role_assignments: z.array(SelectableRoleAssignmentSchema),
            selected_role_assignment_uuid: z.string().uuid().nullable(),
        })
        .strict();

export const SetActiveRoleAssignmentResponseSchema: z.ZodType<SetActiveRoleAssignmentResponse> =
    z.discriminatedUnion('activation_state', [
        z
            .object({
                activation_state: z.literal('operational'),
                role_assignment_uuid: z.string().uuid(),
            })
            .strict(),
        z
            .object({
                activation_state: z.literal('mfa_challenge_required'),
                role_assignment_uuid: z.string().uuid(),
                available_mfa_methods: z.array(MfaMethodSchema),
            })
            .strict(),
        z
            .object({
                activation_state: z.literal('mfa_setup_required'),
                role_assignment_uuid: z.string().uuid(),
                available_mfa_setup_methods: z.array(MfaMethodSchema),
                mfa_setup_activation_target: TotpSetupActivationTargetSchema,
            })
            .strict(),
        z
            .object({
                activation_state: z.literal('legal_acceptance_required'),
                role_assignment_uuid: z.string().uuid(),
                legal_document_assignment_uuid: z.string().uuid(),
                legal_document_uuid: z.string().uuid(),
            })
            .strict(),
    ]);

export const StartImpersonationRequestSchema = z.object({
    role_assignment_uuid: z.string().uuid(),
});

export const ImpersonationContextResponseSchema = z.object({
    impersonated_primary_email: z.string(),
    impersonated_role_assignment_uuid: z.string().uuid(),
    impersonated_role: z.string(),
    impersonated_organization_name: z.string().nullable(),
});

/**
 * Send verification email response
 */
export const SendVerificationEmailResponseSchema = z.object({
    message: z.string(),
    verification_link: z.string(),
});

/**
 * Request password reset request
 */
export const RequestPasswordResetRequestSchema: z.ZodType<RequestPasswordResetRequest> = z.object({
    email: UserEmailIdentitySchema,
});

/**
 * Request password reset response
 */
export const RequestPasswordResetResponseSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    reset_link: z.string().optional(), // For MVP manual distribution
});

export const ValidatePasswordResetTokenRequestSchema: z.ZodObject<{
    token: z.ZodString;
}> = z.object({
    token: z.string().min(1),
});

export const ValidatePasswordResetTokenResponseSchema: z.ZodObject<{
    valid: z.ZodBoolean;
    message: z.ZodString;
}> = z.object({
    valid: z.boolean(),
    message: z.string(),
});

/**
 * Reset password request
 */
export const ResetPasswordRequestSchema = z.object({
    token: z.string().min(1),
    newPassword: z.string().min(1),
});

/**
 * Reset password response
 */
export const ResetPasswordResponseSchema = z.object({
    success: z.boolean(),
    message: z.string(),
});

/**
 * Verify and enable TOTP response
 */
export const VerifyAndEnableTotpResponseSchema = z.object({
    message: z.string(),
    backup_codes: z.array(z.string()),
});

/**
 * Expire session admin response
 */
export const ExpireSessionAdminResponseSchema = z.object({
    message: z.string(),
    user_email: z.string().optional(),
});
